// FlixUiHandler.jsx
// Leaker detection component
// https://github.com/swaglive/swag-webapp/pull/2702
// If user moves video element, app would crash
// If `props.isSuspended` is `ture`, crashing will be suspended until it turns to `false`.

import React from 'react';
import PropTypes from 'prop-types';
import { withScope } from '@sentry/browser';
import trackMixpanel, { EventTypes, getViewId } from '../resource/mixpanel.js';
import { leakerMessage } from '../resource/leaker.js';

export class FlixUiHandler extends React.PureComponent {
  isTriggerPostponed = false;
  postponedTrackArgs = null;
  triggerTimeoutHandle = null;

  isVideo = target => /video/i.test(target);

  /** Track user's leaker behavior */
  /**
   * @param {Object} params
   * @param {'video-moved'|'set-controls'} params.type
   * @param {any} params.controlsValue
   */
  track = ({ type, controlsValue }) => {
    const commonPayload = {
      viewId: getViewId({
        pathname: window.location.pathname,
      }),
      messageId: this.props.messageId,
      userUsername: this.props.userUsername,
    };
    if (type === 'video-moved') {
      trackMixpanel({
        type: EventTypes.USER_VIDEO_LOCATION_CHANGED,
        payload: commonPayload,
      });
    } else if (type === 'set-controls') {
      trackMixpanel({
        type: EventTypes.USER_VIDEO_SET_CONTROLS,
        payload: {
          ...commonPayload,
          controlsValue,
        },
      });
    }
  };

  /** Crash app in random seconds */
  trigger = trackArgs => {
    const { messageId } = this.props;
    // In case of user was tracked when logged-out
    if (this.isTriggerPostponed) {
      this.track(trackArgs);
    }
    this.triggerTimeoutHandle = setTimeout(
      () => {
        // Crash React in random callstack
        React.PureComponent.prototype.componentDidUpdate = () => {
          let fingerprint = null;
          if (trackArgs.type === 'video-moved') {
            fingerprint = ['leaker-move-video-location'];
          } else if (trackArgs.type === 'set-controls') {
            fingerprint = ['leaker-set-video-controls'];
          }
          withScope(scope => {
            if (fingerprint) scope.setFingerprint(fingerprint);
            throw new Error(
              leakerMessage({ message: `messageId: ${messageId}` })
            );
          });
        };
      },
      (Math.random() * 5 + 5) * 1000
    );
  };

  /** @type {MutationCallback} */
  mutationHandler = mutations => {
    let shouldTrigger = false;
    let trackArgs = null;

    for (const mutation of mutations) {
      if (shouldTrigger) break;
      if (mutation.type === 'childList') {
        for (const node of mutation.removedNodes) {
          if (
            this.isVideo(node?.tagName) &&
            //On iOS may render an extra video then delete it
            !this.isVideo(mutation.previousSibling) &&
            !this.triggerTimeoutHandle &&
            !this.isTriggerPostponed
          ) {
            // add whitelist
            if (
              this.props.player?.videoElementDeletedTime ===
              node?.dataset?.deletedTime
            ) {
              continue;
            }

            shouldTrigger = true;
            trackArgs = {
              type: 'video-moved',
            };
            break;
          }
        }
      } else if (mutation.type === 'attributes') {
        if (this.isVideo(mutation.target?.tagName)) {
          if (mutation.attributeName === 'controls') {
            shouldTrigger = true;
            trackArgs = {
              type: 'set-controls',
              controlsValue: mutation.target[mutation.attributeName],
            };
          }
          break;
        }
      }
    }

    if (shouldTrigger) {
      this.track(trackArgs);
      const { isSuspended } = this.props;
      if (isSuspended) {
        this.isTriggerPostponed = true;
        this.postponedTrackArgs = trackArgs;
      } else {
        this.trigger(trackArgs);
      }
    }
  };

  setRootComponent = element => {
    if (!element) return;
    this.observer = new MutationObserver(this.mutationHandler);
    this.observer.observe(element, {
      childList: true,
      subtree: true,
      attributes: true,
    });
  };

  componentDidUpdate(prevProps) {
    if (
      prevProps.isSuspended &&
      !this.props.isSuspended &&
      this.isTriggerPostponed
    ) {
      this.trigger(this.postponedTrackArgs);
    }
  }

  componentWillUnmount() {
    this.observer.disconnect();
    clearTimeout(this.triggerTimeoutHandle);
  }

  render() {
    const { children } = this.props;
    const root = React.Children.only(children);
    return React.cloneElement(root, {
      ref: this.setRootComponent,
    });
  }
}

FlixUiHandler.propTypes = {
  isSuspended: PropTypes.bool.isRequired,
  children: PropTypes.node,
  messageId: PropTypes.string,
  userUsername: PropTypes.string,
  player: PropTypes.object,
};

FlixUiHandler.defaultProps = {
  children: null,
  messageId: '',
  userUsername: '',
  player: {},
};

export default FlixUiHandler;
